/* eslint-disable unicorn/filename-case */
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  type ISettingsColor,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { ButtonStyle, InputBorderStyle } from '../../constants/settings';
import { type CustomCssVarsFn } from '@wix/yoshi-flow-editor';

export type IStylesParams = {
  isInSlot: StyleParamType.Boolean;

  // LAYOUT
  horizontalPadding: StyleParamType.Number;
  verticalPadding: StyleParamType.Number;
  rowSpacing: StyleParamType.Number;
  columnSpacing: StyleParamType.Number;

  // FORM BACKGROUND
  formBackground: StyleParamType.Color;
  borderColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  borderRadius: StyleParamType.Number;
  enableShadow: StyleParamType.Boolean;
  shadowAngle: StyleParamType.Number;
  shadowDistance: StyleParamType.Number;
  shadowSize: StyleParamType.Number;
  shadowBlur: StyleParamType.Number;
  shadowColor: StyleParamType.Color;

  // BUTTONS
  buttonsStyle: StyleParamType.Number;
  buttonsColor: StyleParamType.Color;
  buttonsColorHover: StyleParamType.Color;
  buttonsBackgroundColor: StyleParamType.Color;
  buttonsBackgroundColorHover: StyleParamType.Color;
  buttonsBorderColor: StyleParamType.Color;
  buttonsBorderWidth: StyleParamType.Number;
  buttonsBorderRadius: StyleParamType.Number;
  buttonsFontHover: StyleParamType.Font;

  // SUBMIT BUTTON
  submitButtonStyle: StyleParamType.Number;
  submitButtonFont: StyleParamType.Font;
  submitButtonFontHover: StyleParamType.Font;
  submitButtonColor: StyleParamType.Color;
  submitButtonColorHover: StyleParamType.Color;
  submitButtonBackgroundColor: StyleParamType.Color;
  submitButtonBackgroundColorHover: StyleParamType.Color;
  submitButtonBorderColor: StyleParamType.Color;
  submitButtonBorderColorHover: StyleParamType.Color;
  submitButtonBorderWidth: StyleParamType.Number;
  submitButtonBorderRadius: StyleParamType.Number;

  // PREVIOUS BUTTON
  previousButtonStyle: StyleParamType.Number;
  previousButtonFont: StyleParamType.Font;
  previousButtonFontHover: StyleParamType.Font;
  previousButtonColor: StyleParamType.Color;
  previousButtonColorHover: StyleParamType.Color;
  previousButtonBackgroundColor: StyleParamType.Color;
  previousButtonBackgroundColorHover: StyleParamType.Color;
  previousButtonBorderColor: StyleParamType.Color;
  previousButtonBorderColorHover: StyleParamType.Color;
  previousButtonBorderWidth: StyleParamType.Number;
  previousButtonBorderRadius: StyleParamType.Number;

  // NEXT BUTTON
  nextButtonStyle: StyleParamType.Number;
  nextButtonFont: StyleParamType.Font;
  nextButtonFontHover: StyleParamType.Font;
  nextButtonColor: StyleParamType.Color;
  nextButtonColorHover: StyleParamType.Color;
  nextButtonBackgroundColor: StyleParamType.Color;
  nextButtonBackgroundColorHover: StyleParamType.Color;
  nextButtonBorderColor: StyleParamType.Color;
  nextButtonBorderColorHover: StyleParamType.Color;
  nextButtonBorderWidth: StyleParamType.Number;
  nextButtonBorderRadius: StyleParamType.Number;

  // TEXTS
  headerOneFont: StyleParamType.Font;
  headerOneColor: StyleParamType.Color;
  headerTwoFont: StyleParamType.Font;
  headerTwoColor: StyleParamType.Color;
  paragraphFont: StyleParamType.Font;
  paragraphColor: StyleParamType.Color;

  // INPUT FIELDS
  inputBackgroundColor: StyleParamType.Color;
  inputBackgroundColorHover: StyleParamType.Color;
  inputBorderStyle: StyleParamType.Number;
  inputBorderColor: StyleParamType.Color;
  inputBorderColorHover: StyleParamType.Color;
  inputBorderWidth: StyleParamType.Number;
  inputBorderRadius: StyleParamType.Number;
  inputLabelFont: StyleParamType.Font;
  inputLabelColor: StyleParamType.Color;
  inputValueFont: StyleParamType.Font;
  inputValueColor: StyleParamType.Color;
  inputOptionColor: StyleParamType.Color;
  inputNoteFont: StyleParamType.Font;
  inputNoteColor: StyleParamType.Color;
  inputPlaceholderColor: StyleParamType.Color;
  inputSelectionColor: StyleParamType.Color;
  dropdownBackgroundColor: StyleParamType.Color;
  dropdownOptionTextColor: StyleParamType.Color;
  linkColor: StyleParamType.Color;
  thankYouMessageFont: StyleParamType.Font;
  thankYouMessageColor: StyleParamType.Color;
  inputErrorColor: StyleParamType.Color;
};

interface CustomColorParams {
  value: string;
  opacity?: number;
}

const customColor = ({
  value,
  opacity = 1,
}: CustomColorParams): ISettingsColor => ({
  value,
  name: null,
  opacity,
});

const isInSlot = createStylesParam('isInSlot', {
  type: StyleParamType.Boolean,
  metadata: {
    type: StyleType.booleanValue,
    ai: {
      description:
        'indicates whether the widget is placed in a slot (e.g., blog post slot). When true, padding values are adjusted automatically. This is a Style field, not a Data field.',
    },
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
  getDefaultValue: () => false,
});

const inputBackgroundColor = createStylesParam('inputBackgroundColor', {
  type: StyleParamType.Color,
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: {
      type: 'static',
      preset: 'color-1',
    },
  },
  getDefaultValue: wixColorParam('color-1'),
});

const inputValueColor = createStylesParam('inputValueColor', {
  type: StyleParamType.Color,
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'static',
      preset: 'color-5',
    },
  },
  getDefaultValue: wixColorParam('color-5'),
});

const paragraphFont = createStylesParam('paragraphFont', {
  type: StyleParamType.Font,
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
    },
  },
  getDefaultValue: wixFontParam('Body-M'),
});

const paragraphColor = createStylesParam('paragraphColor', {
  type: StyleParamType.Color,
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'static',
      preset: 'color-5',
    },
  },
  getDefaultValue: wixColorParam('color-5'),
});

export default createStylesParams<IStylesParams>({
  isInSlot,
  // LAYOUT
  horizontalPadding: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
    // TODO: will blog use slots on editor 3?
    getDefaultValue: (props) => {
      const isWidgetInSlot = props.getStyleParamValue(isInSlot);
      return isWidgetInSlot ? 0 : 24;
    },
  },
  verticalPadding: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
    // TODO: will blog use slots on editor 3?
    getDefaultValue: (props) => {
      const isWidgetInSlot = props.getStyleParamValue(isInSlot);
      return isWidgetInSlot ? 40 : 24;
    },
  },
  // TODO: gap type creates horizontal & vertical sliders, only one of them should remain
  rowSpacing: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
    getDefaultValue: () => 24,
  },
  columnSpacing: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 24,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 5,
      },
    },
    getDefaultValue: ({ isMobile }) => (isMobile ? 5 : 24),
  },

  // FORM BACKGROUND
  formBackground: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  borderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-5', 0),
  },
  borderWidth: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  borderRadius: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  enableShadow: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.boxShadow,
      // the type allows any values & auto panel preview does not handle default values yet
      // TODO: check if default values are correct
      defaultValue: {
        type: 'static',
        inset: false,
        x: 0,
        y: 0,
        blur: 25,
        spread: 0,
        color: 'color-5',
        opacity: 0.15,
        angle: 135,
      },
    },
    getDefaultValue: () => false,
  },
  shadowAngle: {
    type: StyleParamType.Number,
    getDefaultValue: () => 135,
  },
  shadowDistance: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  shadowSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  shadowBlur: {
    type: StyleParamType.Number,
    getDefaultValue: () => 25,
  },
  shadowColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.15),
  },

  // BUTTONS
  buttonsStyle: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      ai: {
        description:
          'controls the visual style of buttons. Valid options: 0 (Rectangle outline), 1 (RectangleRoundedCorners outline), 2 (RectangleFilled), 3 (RectangleRoundedCornersFilled), 4 (ABC/text link style). Must be one of these numeric values.',
      },
      defaultValue: {
        type: 'static',
        value: ButtonStyle.RectangleFilled,
      },
    },
    getDefaultValue: () => ButtonStyle.RectangleFilled,
  },
  buttonsFontHover: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      lineHeight: 16,
      // TODO: add style to metadata
      style: { underline: false, italic: false, bold: false },
    }),
  },
  buttonsColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonsBackgroundColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonsBorderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-1', 0),
  },
  buttonsBorderWidth: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  buttonsBorderRadius: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  buttonsColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonsBackgroundColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
        opacity: 0.7,
      },
    },
    getDefaultValue: wixColorParam('color-8', 0.7),
  },

  // SUBMIT BUTTON
  submitButtonStyle: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      ai: {
        description:
          'controls the visual style of the submit button. Valid options: 0 (Rectangle outline), 1 (RectangleRoundedCorners outline), 2 (RectangleFilled), 3 (RectangleRoundedCornersFilled), 4 (ABC/text link style). Must be one of these numeric values.',
      },
      defaultValue: {
        type: 'static',
        value: ButtonStyle.RectangleFilled,
      },
    },
    getDefaultValue: () => ButtonStyle.RectangleFilled,
  },
  submitButtonFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      // TODO: add style to metadata
      style: { underline: false, italic: false, bold: false },
    }),
  },
  submitButtonFontHover: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      // TODO: add style to metadata
      style: { underline: false, italic: false, bold: false },
    }),
  },
  submitButtonColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  submitButtonBackgroundColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
    getDefaultValue: wixColorParam('color-8'),
  },
  submitButtonBorderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-1', 0),
  },
  submitButtonBorderWidth: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  submitButtonBorderRadius: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  submitButtonColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  submitButtonBackgroundColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
        opacity: 0.7,
      },
    },
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  submitButtonBorderColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-1', 0),
  },

  // NEXT BUTTON
  nextButtonStyle: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      ai: {
        description:
          'controls the visual style of the next button. Valid options: 0 (Rectangle outline), 1 (RectangleRoundedCorners outline), 2 (RectangleFilled), 3 (RectangleRoundedCornersFilled). Note: option 4 (ABC/text link) is not available for next button. Must be one of these numeric values.',
      },
      defaultValue: {
        type: 'static',
        value: ButtonStyle.RectangleFilled,
      },
    },
    getDefaultValue: () => ButtonStyle.RectangleFilled,
  },
  nextButtonFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  nextButtonFontHover: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      // TODO: add style to metadata
      style: { underline: false, italic: false, bold: false },
    }),
  },
  nextButtonColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  nextButtonBackgroundColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
    getDefaultValue: wixColorParam('color-8'),
  },
  nextButtonBorderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-1', 0),
  },
  nextButtonBorderWidth: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  nextButtonBorderRadius: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  nextButtonColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  nextButtonBackgroundColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
        opacity: 0.7,
      },
    },
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  nextButtonBorderColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-1', 0),
  },

  // PREVIOUS BUTTON
  previousButtonStyle: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      ai: {
        description:
          'controls the visual style of the previous button. Valid options: 0 (Rectangle outline), 1 (RectangleRoundedCorners outline), 2 (RectangleFilled), 3 (RectangleRoundedCornersFilled). Note: option 4 (ABC/text link) is not available for previous button. Must be one of these numeric values.',
      },
      defaultValue: {
        type: 'static',
        value: ButtonStyle.RectangleFilled,
      },
    },
    getDefaultValue: () => ButtonStyle.RectangleFilled,
  },
  previousButtonFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  previousButtonFontHover: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      // TODO: add style to metadata
      style: { underline: false, italic: false, bold: false },
    }),
  },
  previousButtonColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
    getDefaultValue: wixColorParam('color-5'),
  },
  previousButtonBackgroundColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-8', 0),
  },
  previousButtonBorderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-5'),
  },
  previousButtonBorderWidth: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
    getDefaultValue: () => 1,
  },
  previousButtonBorderRadius: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  previousButtonColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  previousButtonBackgroundColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
        opacity: 0.7,
      },
    },
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  previousButtonBorderColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
    getDefaultValue: wixColorParam('color-1', 0),
  },

  // TEXTS
  headerOneFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Heading-M',
      },
    },
    getDefaultValue: wixFontParam('Heading-M'),
  },
  headerOneColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
    getDefaultValue: wixColorParam('color-5'),
  },
  headerTwoFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Heading-S',
      },
    },
    getDefaultValue: wixFontParam('Heading-S'),
  },
  headerTwoColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
    getDefaultValue: wixColorParam('color-5'),
  },
  paragraphFont,
  paragraphColor,

  // INPUT FIELDS
  inputBackgroundColor,
  inputBackgroundColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'ref',
        ref: 'inputBackgroundColor',
      },
    },
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(inputBackgroundColor) ?? wixColorParam('color-1'),
  },
  inputBorderStyle: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      ai: {
        description:
          'controls the border style for input fields. Valid options: 0 (None - no border), 1 (AllSides - border on all sides), 2 (Bottom - border on bottom only). Must be one of these numeric values.',
      },
      defaultValue: {
        type: 'static',
        value: InputBorderStyle.AllSides,
      },
    },
    getDefaultValue: () => InputBorderStyle.AllSides,
  },
  inputBorderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.6,
      },
    },
    getDefaultValue: wixColorParam('color-5', 0.6),
  },
  inputBorderColorHover: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
    getDefaultValue: wixColorParam('color-5'),
  },
  inputBorderWidth: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
    getDefaultValue: () => 1,
  },
  inputBorderRadius: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
    getDefaultValue: () => 0,
  },
  inputLabelFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  inputLabelColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
    getDefaultValue: wixColorParam('color-5'),
  },
  inputValueFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-L',
        size: 16,
      },
    },
    getDefaultValue: wixFontParam('Body-L', { size: 16, lineHeight: 24 }),
  },
  inputValueColor,
  inputOptionColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'color for option text in fields without a box (CHECKBOX, RADIO_GROUP, CHECKBOX_GROUP, CONTACTS_SUBSCRIBE). Defaults to inputValueColor (input text color).',
      },
      defaultValue: {
        type: 'ref',
        ref: 'inputValueColor',
      },
    },
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(inputValueColor) ?? wixColorParam('color-5'),
  },
  inputNoteFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  inputNoteColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-4',
      },
    },
    getDefaultValue: wixColorParam('color-4'),
  },
  inputPlaceholderColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-4',
      },
    },
    getDefaultValue: wixColorParam('color-4'),
  },
  inputSelectionColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
    getDefaultValue: wixColorParam('color-8'),
  },
  dropdownBackgroundColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      ai: {
        description:
          'background color and opacity for the dropdown popover (the options list). Defaults to inputBackgroundColor (field fill color).',
      },
      defaultValue: {
        type: 'ref',
        ref: 'inputBackgroundColor',
      },
    },
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(inputBackgroundColor) ?? wixColorParam('color-1'),
  },
  dropdownOptionTextColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'text color for options in the dropdown popover list. Defaults to inputValueColor (input text color).',
      },
      defaultValue: {
        type: 'ref',
        ref: 'inputValueColor',
      },
    },
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(inputValueColor) ?? wixColorParam('color-5'),
  },
  linkColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
    getDefaultValue: wixColorParam('color-8'),
  },
  thankYouMessageFont: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'paragraphFont',
      },
    },
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(paragraphFont) ?? wixFontParam('Body-M'),
  },
  thankYouMessageColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'paragraphColor',
      },
    },
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(paragraphColor) ?? wixColorParam('color-5'),
  },
  inputErrorColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: customColor({ value: '#df3131' }).value,
        opacity: customColor({ value: '#df3131' }).opacity,
      },
    },
    getDefaultValue: () => customColor({ value: '#df3131' }),
  },
});

type CssVarsParams = Parameters<CustomCssVarsFn>[0]['styleParams'];

const getInputBorderWidths = ({
  styleParams,
}: {
  styleParams: CssVarsParams;
}) => {
  const borderWidth = styleParams.numbers.inputBorderWidth;
  const borderStyle = styleParams.numbers.inputBorderStyle;

  const defaultBorders = {
    inputBorderLeftWidth: borderWidth,
    inputBorderRightWidth: borderWidth,
    inputBorderTopWidth: borderWidth,
    inputBorderBottomWidth: borderWidth,
  };

  const borders = {
    [InputBorderStyle.None]: {
      inputBorderLeftWidth: 0,
      inputBorderRightWidth: 0,
      inputBorderTopWidth: 0,
      inputBorderBottomWidth: 0,
    },
    [InputBorderStyle.Bottom]: {
      inputBorderLeftWidth: 0,
      inputBorderRightWidth: 0,
      inputBorderTopWidth: 0,
      inputBorderBottomWidth: borderWidth,
    },
    [InputBorderStyle.AllSides]: defaultBorders,
  }[borderStyle];

  return borders || defaultBorders;
};

export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const inputBorderWidths = getInputBorderWidths({ styleParams });
  const isSubmitTextButton =
    styleParams.numbers.submitButtonStyle === ButtonStyle.ABC;

  return {
    ...inputBorderWidths,
    ...(isSubmitTextButton
      ? {
          submitButtonBackgroundColor: '0,0,0,0',
          'submitButtonBackgroundColor-opacity': '0',
          submitButtonBackgroundColorHover: '0,0,0,0',
          'submitButtonBackgroundColorHover-opacity': '0',
        }
      : {}),
  };
};
